import React, { useState, useMemo } from "react";
import type {
  PatientSymptom,
  VitalsHomeResponse,
  PatientInfo,
} from "@/types/appointments";
import { MetricChartModal } from "@/components/MetricChartModal";
import type { HealthMetric } from "@/types/tracker";
import { VitalsMetricCard } from "@/components/consultation/cards/VitalsMetricCard";
import { groupSymptomsByType } from "@/lib/symptoms";
import {
  formatBloodPressureRange,
  formatVitalRange,
  hasVitalData,
  deriveChartUser,
} from "@/lib/vitals-utils";
import { EmptyState } from "@/components/consultation/EmptyState";
import {
  DismissibleChip,
  ChipContainer,
} from "@/components/consultation/shared/DismissibleChip";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";

interface SymptomsHomeProps {
  vitalsData?: VitalsHomeResponse;
  patientSymptoms: PatientSymptom[];
  patientInfo?: PatientInfo;
  onAddVitals: () => void;
  onAddSymptoms: () => void;
  onDeleteSymptom: (symptomId: string) => void;
  isCompleted?: boolean;
}

export const SymptomsHome = ({
  vitalsData,
  patientSymptoms,
  patientInfo,
  onAddVitals,
  onAddSymptoms,
  onDeleteSymptom,
  isCompleted = false,
}: SymptomsHomeProps) => {
  const [selectedMetric, setSelectedMetric] = useState<HealthMetric | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const chartUser = useMemo(() => deriveChartUser(patientInfo), [patientInfo]);

  // Symptom drafts use activity_status; inactive items must stay hidden until
  // the prescription/meal-plan publish flushes them to PatientSymptoms.
  const activeSymptoms = useMemo(
    () => patientSymptoms.filter((s) => s.activity_status === "active"),
    [patientSymptoms]
  );

  // Drop empty buckets so the layout collapses gracefully when a category has
  // no symptoms recorded for this appointment.
  const symptomGroups = useMemo(
    () => groupSymptomsByType(activeSymptoms).filter((g) => g.items.length > 0),
    [activeSymptoms]
  );

  const openChart = (
    metricId: string,
    title: string,
    value: string,
    unit: string,
    category: any
  ) => {
    setSelectedMetric({
      id: metricId,
      title,
      value,
      unit,
      period: "Last 7 days",
      category,
    });
    setIsModalOpen(true);
  };

  type VitalsCardData = {
    title: string;
    value: string;
    unit: string;
    periodLabel: string;
    onClick: () => void;
  };

  const vitalsCards: VitalsCardData[] = [];

  if (vitalsData?.blood_pressure && hasVitalData(vitalsData.blood_pressure)) {
    vitalsCards.push({
      title: "Blood Pressure",
      value: formatBloodPressureRange(vitalsData.blood_pressure),
      unit: "mmHg",
      periodLabel: "Last 7 Days",
      onClick: () =>
        openChart(
          "blood-pressure",
          "Blood Pressure",
          formatBloodPressureRange(vitalsData.blood_pressure),
          "mmHg",
          "heart-health"
        ),
    });
  }

  if (vitalsData?.heart_rate && hasVitalData(vitalsData.heart_rate)) {
    vitalsCards.push({
      title: "Heart Rate",
      value: formatVitalRange(
        vitalsData.heart_rate.min,
        vitalsData.heart_rate.max
      ),
      unit: "bpm",
      periodLabel: "Last 24 Hours",
      onClick: () =>
        openChart(
          "heart-rate",
          "Heart Rate",
          formatVitalRange(
            vitalsData.heart_rate.min,
            vitalsData.heart_rate.max
          ),
          "bpm",
          "heart-health"
        ),
    });
  }

  if (vitalsData?.blood_glucose && hasVitalData(vitalsData.blood_glucose)) {
    vitalsCards.push({
      title: "Blood Glucose",
      value: formatVitalRange(
        vitalsData.blood_glucose.min,
        vitalsData.blood_glucose.max
      ),
      unit: "mg/dL",
      periodLabel: "Last 24 Hours",
      onClick: () =>
        openChart(
          "blood-glucose",
          "Blood Glucose",
          formatVitalRange(
            vitalsData.blood_glucose.min,
            vitalsData.blood_glucose.max
          ),
          "mg/dL",
          "diabetes"
        ),
    });
  }

  if (vitalsData?.temperature && hasVitalData(vitalsData.temperature)) {
    vitalsCards.push({
      title: "Temperature",
      value: formatVitalRange(
        vitalsData.temperature.min,
        vitalsData.temperature.max
      ),
      unit: "°F",
      periodLabel: "Last 7 Days",
      onClick: () =>
        openChart(
          "temperature",
          "Temperature",
          formatVitalRange(
            vitalsData.temperature.min,
            vitalsData.temperature.max
          ),
          "°F",
          "all"
        ),
    });
  }

  if (
    vitalsData?.respiratory_rate &&
    hasVitalData(vitalsData.respiratory_rate)
  ) {
    vitalsCards.push({
      title: "Resp. Rate",
      value: formatVitalRange(
        vitalsData.respiratory_rate.min,
        vitalsData.respiratory_rate.max
      ),
      unit: "rpm",
      periodLabel: "Last 7 Days",
      onClick: () =>
        openChart(
          "respiratory-rate",
          "Respiratory Rate",
          formatVitalRange(
            vitalsData.respiratory_rate.min,
            vitalsData.respiratory_rate.max
          ),
          "rpm",
          "respiratory-rate"
        ),
    });
  }

  return (
    <div className="h-full overflow-hidden flex flex-col gap-3 p-4">
      {/* Vitals */}
      <Widget>
        <WidgetHeader
          title="Vitals"
          onAdd={isCompleted ? undefined : onAddVitals}
          hasInfo
        />
        <WidgetContent>
          {vitalsCards.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
              {vitalsCards.map((card, index) => (
                <VitalsMetricCard
                  key={index}
                  title={card.title}
                  value={card.value}
                  unit={card.unit}
                  periodLabel={card.periodLabel}
                  onClick={card.onClick}
                />
              ))}
            </div>
          ) : (
            <EmptyState title="No vitals data available" variant="compact" />
          )}
        </WidgetContent>
      </Widget>

      {/* Symptoms */}
      <Widget>
        <WidgetHeader
          title="Symptoms Observed"
          onAdd={isCompleted ? undefined : onAddSymptoms}
          hasInfo
        />
        <WidgetContent>
          {symptomGroups.length > 0 ? (
            <div className="space-y-2">
              {symptomGroups.map((group) => (
                <div
                  key={group.key}
                  className="rounded-xl border border-border/50 bg-muted/20 p-3 space-y-1.5"
                >
                  <p className="z-label text-muted-foreground">{group.label}</p>
                  <ChipContainer>
                    {group.items.map((symptom) => (
                      <DismissibleChip
                        key={symptom.id}
                        label={
                          symptom.symptoms?.name ??
                          symptom.custom_symptoms ??
                          "Custom symptom"
                        }
                        onRemove={
                          isCompleted
                            ? undefined
                            : () => onDeleteSymptom(symptom.id)
                        }
                        className="border-warning/30 bg-warning/10 text-warning"
                      />
                    ))}
                  </ChipContainer>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No symptoms recorded" variant="compact" />
          )}
        </WidgetContent>
      </Widget>

      {/* Chart Modal */}
      <MetricChartModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        metric={selectedMetric}
        selectedPatient={chartUser}
        isCompleted={isCompleted}
      />
    </div>
  );
};
